import React, { useState, useEffect } from "react";
import { Box, Modal, Typography, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UserItem from "./UserItem";
import { styles } from "./stylesFollowersModal";
import {
  useGetUserFollowersQuery,
  useGetUserFollowingQuery,
  UserListItem,
} from "@/store/api/userApi";

const LIMIT = 20;

interface FollowersModalProps {
  open: boolean;
  onClose: () => void;
  uid: string;
  followersCount: number;
  followingCount: number;
  initialTab?: "followers" | "following";
}

const FollowersModal: React.FC<FollowersModalProps> = ({
  open,
  onClose,
  uid,
  followersCount,
  followingCount,
  initialTab = "followers",
}) => {
  const [activeTab, setActiveTab] = useState<"followers" | "following">(initialTab);
  const [page, setPage] = useState(1);
  const [allUsers, setAllUsers] = useState<UserListItem[]>([]);
  const [hasMore, setHasMore] = useState(true);

  const {
    data: followersData,
    isLoading: isFollowersLoading,
    isFetching: isFollowersFetching,
  } = useGetUserFollowersQuery(
    { uid, page, limit: LIMIT },
    { skip: activeTab !== "followers" || !open }
  );

  const {
    data: followingData,
    isLoading: isFollowingLoading,
    isFetching: isFollowingFetching,
  } = useGetUserFollowingQuery(
    { uid, page, limit: LIMIT },
    { skip: activeTab !== "following" || !open }
  );

  const isLoading =
    activeTab === "followers"
      ? isFollowersLoading || isFollowersFetching
      : isFollowingLoading || isFollowingFetching;

  const responseData = activeTab === "followers" ? followersData : followingData;

  useEffect(() => {
    if (!open) return;
    setActiveTab(initialTab);
    setPage(1);
    setAllUsers([]);
    setHasMore(true);
  }, [open, initialTab]);

  useEffect(() => {
    setPage(1);
    setAllUsers([]);
    setHasMore(true);
  }, [activeTab]);

  useEffect(() => {
    if (!responseData) return;
    const items = responseData.data ?? [];

    setAllUsers((prev) => {
      if (page === 1) return items;
      const seen = new Set(prev.map((u) => u.uid));
      return [...prev, ...items.filter((u) => !seen.has(u.uid))];
    });

    setHasMore(items.length === LIMIT);
  }, [responseData, page]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollTop + clientHeight >= scrollHeight - 10 && hasMore && !isLoading) {
      setPage((prev) => prev + 1);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={styles.modalContainer}>
        <Box sx={styles.modalContent}>
          <Box sx={styles.modalHeader}>
            <Typography sx={styles.headerText}>Users</Typography>
            <IconButton sx={styles.closeBtn} onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={styles.usersContent}>
            <Box sx={styles.tabsWrapper}>
              <Box
                sx={activeTab === "following" ? styles.tabActive : styles.tabInactive}
                onClick={() => setActiveTab("following")}
              >
                Following ({followingCount.toLocaleString()})
              </Box>
              <Box
                sx={activeTab === "followers" ? styles.tabActive : styles.tabInactive}
                onClick={() => setActiveTab("followers")}
              >
                Followers ({followersCount.toLocaleString()})
              </Box>
            </Box>

            <Box sx={styles.usersBody} className="hide-scrollbar" onScroll={handleScroll}>
              {isLoading && allUsers.length === 0 ? (
                <Typography sx={{ color: "#9A9FA5", textAlign: "center", mt: 4 }}>
                  Loading...
                </Typography>
              ) : allUsers.length === 0 ? (
                <Typography sx={{ color: "#9A9FA5", textAlign: "center", mt: 4 }}>
                  No users found
                </Typography>
              ) : (
                allUsers.map((user) => (
                  <UserItem
                    key={user.uid}
                    username={user.username}
                    avatar={user.image?.url ?? null}
                    // bio={user.bio ?? ""}
                    uid={user.uid}
                  />
                ))
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default FollowersModal;
